import React from 'react';
import {
  FailedTrackedQuery,
  getFailedTrackedQueriesForOwner,
  subscribeFailedTrackedQueries,
} from '@bilderit/offline-link';
import { useCurrentIdentity } from '@/utils/currentIdentity';

/**
 * The mutations the offline queue could not deliver because the server
 * rejected them for good (see `moveTrackedQueryToFailed` in
 * @bilderit/offline-link), for the signed-in identity. Read from the device's
 * storage on mount and again on every change to the failed store (a
 * rejection, a discard, a retry). Empty while there is no identity.
 */
export const useFailedTrackedQueries = (): FailedTrackedQuery[] => {
  const owner = useCurrentIdentity();
  const [failed, setFailed] = React.useState<FailedTrackedQuery[]>([]);

  React.useEffect(() => {
    if (!owner) {
      setFailed([]);
      return;
    }

    let active = true;
    // Only the latest read may set the state: a slower earlier one would
    // otherwise bring back what a later change already took out.
    let latestRead = 0;
    const load = () => {
      const read = ++latestRead;
      getFailedTrackedQueriesForOwner(owner)
        .then((entries) => {
          if (active && read === latestRead) setFailed(entries);
        })
        .catch((err: unknown) =>
          console.error('getFailedTrackedQueriesForOwner failed', err)
        );
    };

    load();
    const unsubscribe = subscribeFailedTrackedQueries(load);
    return () => {
      active = false;
      unsubscribe();
    };
  }, [owner]);

  return failed;
};
